import { useState, useEffect, useCallback, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Pagination,
  Stack,
  Alert,
  Autocomplete,
  TextField,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import Visibility from '@mui/icons-material/Visibility'
import CheckCircle from '@mui/icons-material/CheckCircle'
import Refresh from '@mui/icons-material/Refresh'
import Clear from '@mui/icons-material/Clear'
import ArrowBack from '@mui/icons-material/ArrowBack'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import { chamberRoomAuditAPI, REQUEST_STATUSES, AUDIT_ERROR_CODES } from 'src/supportPortal/services/chamberRoomAuditService'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import { toast } from 'react-toastify'
import AuditRequestDetailsDrawer from './components/AuditRequestDetailsDrawer'
import ApproveRejectDialog from './components/ApproveRejectDialog'

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
}))

const StatusChip = styled(Chip)(({ theme, status }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'PENDING':
        return { bg: '#fff3cd', color: '#856404', border: '#ffc107' }
      case 'APPROVED':
        return { bg: '#d4edda', color: '#155724', border: '#28a745' }
      case 'REJECTED':
      case 'DENIED':
        return { bg: '#f8d7da', color: '#721c24', border: '#dc3545' }
      case 'EXPIRED':
        return { bg: '#e2e3e5', color: '#383d41', border: '#6c757d' }
      case 'CANCELLED':
      case 'DELETED':
        return { bg: '#f5f5f5', color: '#6c757d', border: '#adb5bd' }
      default:
        return { bg: theme.palette.grey[100], color: theme.palette.grey[700], border: theme.palette.grey[400] }
    }
  }
  const colors = getStatusColor()
  return {
    backgroundColor: colors.bg,
    color: colors.color,
    border: `1px solid ${colors.border}`,
    fontWeight: 600,
    fontSize: '0.75rem',
  }
})

const FiltersContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
}))

// Helper to get date 30 days ago in ISO format
const getDate30DaysAgo = () => {
  const date = new Date()
  date.setDate(date.getDate() - 30)
  return date.toISOString().slice(0, 16) // Format for datetime-local input
}

// Helper to convert datetime-local to ISO-8601 UTC
const toISOString = (dateTimeLocal) => {
  if (!dateTimeLocal) return null
  const date = new Date(dateTimeLocal)
  return date.toISOString()
}

export default function AuditRequestsListing({
  isFromTherapists = false,
  onBack = null,
  embedded = false,
}) {
  const location = useLocation()
  const navigate = useNavigate()
  const [auditRequests, setAuditRequests] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [pagination, setPagination] = useState({
    page: 0,
    size: 20,
    totalElements: 0,
    totalPages: 0,
  })

  // Dropdown options
  const [allMentors, setAllMentors] = useState([])
  const [allChamberRooms, setAllChamberRooms] = useState([])
  const [loadingOptions, setLoadingOptions] = useState(false)

  // Parse URL query parameters for pre-populated filters
  const getInitialFilters = useCallback(() => {
    const params = new URLSearchParams(location.search)
    const initialFilters = {
      chamberRoomIds: [],
      mentorToAuditIds: [],
      auditorMentorIds: [],
      requestStatuses: [],
      createdAfter: getDate30DaysAgo(), // Default to last 30 days
      createdBefore: '',
    }

    // Handle chamberRoomIds (can be multiple)
    const chamberRoomIds = params.getAll('chamberRoomIds')
    if (chamberRoomIds.length > 0) {
      initialFilters.chamberRoomIds = chamberRoomIds.filter((id) => id?.trim())
    }

    // Handle mentorToAuditIds (can be multiple)
    const mentorToAuditIds = params.getAll('mentorToAuditIds')
    if (mentorToAuditIds.length > 0) {
      initialFilters.mentorToAuditIds = mentorToAuditIds.filter((id) => id?.trim())
    }

    // Handle auditorMentorIds (can be multiple) - only if not from therapists
    const auditorMentorIds = params.getAll('auditorMentorIds')
    if (auditorMentorIds.length > 0 && !isFromTherapists) {
      initialFilters.auditorMentorIds = auditorMentorIds.filter((id) => id?.trim())
    } else if (auditorMentorIds.length > 0 && isFromTherapists) {
      // If from therapists, set it but don't show the filter
      initialFilters.auditorMentorIds = auditorMentorIds.filter((id) => id?.trim())
    }

    // Handle requestStatuses (can be multiple)
    const requestStatuses = params.getAll('requestStatuses')
    if (requestStatuses.length > 0) {
      initialFilters.requestStatuses = requestStatuses
    }

    // Override default if URL has date params
    if (params.get('createdAfter')) {
      initialFilters.createdAfter = params.get('createdAfter')
    }
    if (params.get('createdBefore')) {
      initialFilters.createdBefore = params.get('createdBefore')
    }

    return initialFilters
  }, [location.search, isFromTherapists])

  // Filters state
  const [filters, setFilters] = useState(getInitialFilters)

  // UI state
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false)
  const [selectedRequest, setSelectedRequest] = useState(null)
  const [approveRejectDialogOpen, setApproveRejectDialogOpen] = useState(false)
  const [requestToApproveReject, setRequestToApproveReject] = useState(null)

  // Fetch dropdown options
  const fetchDropdownOptions = useCallback(async () => {
    setLoadingOptions(true)
    try {
      // Fetch mentors
      const mentorsResp = await getSPDataWithParams('/api/secure/cc/support/mentors/list', {
        mentorEventTypes: 'therapy-session,consultation',
        sendAssociatedChambers: false,
      })
      if (mentorsResp && isCommonResponseSuccessfulV2(mentorsResp)) {
        const mentors = getDataMapFromCommonResponse(mentorsResp)?.mentor || []
        setAllMentors(mentors)
      }

      // Fetch chamber rooms - TODO: Replace with actual API when available
      // For now, extract unique chamber rooms from audit requests
      // This will be populated after first fetch
    } catch (err) {
      console.error('Error fetching dropdown options:', err)
    } finally {
      setLoadingOptions(false)
    }
  }, [])

  // Extract unique chamber rooms from audit requests
  useEffect(() => {
    if (auditRequests.length > 0) {
      const uniqueRooms = Array.from(
        new Map(
          auditRequests
            .filter((req) => req.chamberRoomId && req.chamberRoomTitle)
            .map((req) => [req.chamberRoomId, { id: req.chamberRoomId, title: req.chamberRoomTitle }])
        ).values()
      )
      setAllChamberRooms((prev) => {
        const existingIds = new Set(prev.map((r) => r.id))
        const newRooms = uniqueRooms.filter((r) => !existingIds.has(r.id))
        return [...prev, ...newRooms]
      })
    }
  }, [auditRequests])

  // Fetch audit requests
  const fetchAuditRequests = useCallback(async (page = 0) => {
    setLoading(true)
    setError(null)

    try {
      const apiFilters = {
        ...filters,
        page,
        size: pagination.size,
        sort: 'createdTime,desc',
      }

      // Convert createdAfter/createdBefore to ISO-8601 if they exist
      if (apiFilters.createdAfter) {
        apiFilters.createdAfter = toISOString(apiFilters.createdAfter)
      }
      if (apiFilters.createdBefore) {
        apiFilters.createdBefore = toISOString(apiFilters.createdBefore)
      }

      // Note: API uses createdTime filter, but we're using createdAfter/createdBefore
      // If API doesn't support these, we may need to filter client-side or ask for API support
      // For now, removing these from API call and will need to confirm with user
      delete apiFilters.createdAfter
      delete apiFilters.createdBefore

      const response = await chamberRoomAuditAPI.searchAuditRequests(apiFilters)

      if (response && isCommonResponseSuccessfulV2(response)) {
        const data = getDataMapFromCommonResponse(response)
        let content = data?.content || []

        // Client-side filter for last 30 days if createdAfter is set
        if (filters.createdAfter) {
          const thirtyDaysAgo = new Date(filters.createdAfter)
          content = content.filter((req) => {
            if (!req.createdTime) return false
            const createdDate = new Date(req.createdTime)
            return createdDate >= thirtyDaysAgo
          })
        }

        setAuditRequests(content)
        setPagination((prev) => ({
          ...prev,
          page,
          totalElements: content.length, // Use filtered count
          totalPages: Math.ceil(content.length / pagination.size),
        }))
      } else {
        const errorCode = response?.response?.data?.code
        const errorMessage = AUDIT_ERROR_CODES[errorCode] || 'Failed to fetch audit requests'
        setError(errorMessage)
        setAuditRequests([])
      }
    } catch (err) {
      console.error('Error fetching audit requests:', err)
      const errorCode = err?.response?.data?.code
      const errorMessage = AUDIT_ERROR_CODES[errorCode] || 'An error occurred while fetching audit requests'
      setError(errorMessage)
      setAuditRequests([])
    } finally {
      setLoading(false)
    }
  }, [filters, pagination.size])

  // Initialize filters from URL on mount
  useEffect(() => {
    const initialFilters = getInitialFilters()
    setFilters(initialFilters)
  }, [getInitialFilters])

  // Fetch dropdown options on mount
  useEffect(() => {
    fetchDropdownOptions()
  }, [fetchDropdownOptions])

  useEffect(() => {
    fetchAuditRequests(0)
  }, [fetchAuditRequests])

  // Filter handlers
  const handleChamberRoomChange = (event, newValue) => {
    setFilters((prev) => ({
      ...prev,
      chamberRoomIds: newValue ? [newValue.id] : [],
    }))
  }

  const handleMentorToAuditChange = (event, newValue) => {
    setFilters((prev) => ({
      ...prev,
      mentorToAuditIds: newValue ? [newValue.id] : [],
    }))
  }

  const handleAuditorMentorChange = (event, newValue) => {
    setFilters((prev) => ({
      ...prev,
      auditorMentorIds: newValue ? [newValue.id] : [],
    }))
  }

  const handleStatusChange = (event) => {
    setFilters((prev) => ({
      ...prev,
      requestStatuses: event.target.value,
    }))
  }

  const handleDateRangeChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleClearFilters = () => {
    const defaultFilters = {
      chamberRoomIds: [],
      mentorToAuditIds: [],
      auditorMentorIds: isFromTherapists ? filters.auditorMentorIds : [], // Keep if from therapists
      requestStatuses: [],
      createdAfter: getDate30DaysAgo(), // Reset to default 30 days
      createdBefore: '',
    }
    setFilters(defaultFilters)
  }

  const hasActiveFilters = useMemo(() => {
    return (
      filters.chamberRoomIds.length > 0 ||
      filters.mentorToAuditIds.length > 0 ||
      (!isFromTherapists && filters.auditorMentorIds.length > 0) ||
      filters.requestStatuses.length > 0 ||
      filters.createdAfter !== getDate30DaysAgo() ||
      filters.createdBefore
    )
  }, [filters, isFromTherapists])

  // Action handlers
  const handleViewDetails = (request) => {
    setSelectedRequest(request)
    setDetailsDrawerOpen(true)
  }

  const handleApproveReject = (request) => {
    setRequestToApproveReject(request)
    setApproveRejectDialogOpen(true)
  }

  const handleApproveRejectSuccess = () => {
    setApproveRejectDialogOpen(false)
    setRequestToApproveReject(null)
    fetchAuditRequests(pagination.page)
    toast.success('Request updated successfully')
  }

  const handlePageChange = (event, newPage) => {
    fetchAuditRequests(newPage - 1)
  }

  const handleBackClick = () => {
    if (onBack) {
      onBack()
    } else if (isFromTherapists) {
      navigate('/support-home/practitioner-management/therapists')
    } else {
      navigate(-1)
    }
  }

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '-'
    try {
      const date = new Date(dateTimeString)
      if (Number.isNaN(date.getTime())) return '-'
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    } catch {
      return dateTimeString
    }
  }

  const isExpired = (expiryAt) => {
    if (!expiryAt) return false
    try {
      return new Date(expiryAt) < new Date()
    } catch {
      return false
    }
  }

  // Get selected values for dropdowns
  const selectedChamberRoom = filters.chamberRoomIds.length > 0
    ? allChamberRooms.find((r) => r.id === filters.chamberRoomIds[0])
    : null

  const selectedMentorToAudit = filters.mentorToAuditIds.length > 0
    ? allMentors.find((m) => m.id === filters.mentorToAuditIds[0])
    : null

  const selectedAuditorMentor = filters.auditorMentorIds.length > 0
    ? allMentors.find((m) => m.id === filters.auditorMentorIds[0])
    : null

  return (
    <>
      {!embedded && (
        <SupportHeader
          heading="Audit Requests"
          showBackButton={!onBack}
          backPath={isFromTherapists ? '/support-home/practitioner-management/therapists' : undefined}
        />
      )}
      {onBack && !embedded && (
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBackClick}
            sx={{ textTransform: 'none' }}
          >
            Back to Audit Management
          </Button>
        </Box>
      )}
      <Box
        sx={{
          width: '100%',
          padding: embedded ? { xs: '12px', sm: '16px' } : { xs: '16px', sm: '24px', md: '32px' },
          maxWidth: '1600px',
          margin: '0 auto',
        }}
      >
        {/* Filters */}
        <FiltersContainer>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>
              Filters
            </Typography>
            {hasActiveFilters && (
              <Button
                size="small"
                startIcon={<Clear />}
                onClick={handleClearFilters}
                sx={{ textTransform: 'none' }}
              >
                Clear All
              </Button>
            )}
          </Box>

          <Stack spacing={2}>
            {/* Chamber Room - Dropdown */}
            <FormControl size="small" fullWidth>
              <Autocomplete
                options={allChamberRooms}
                getOptionLabel={(option) => `${option.title} (${option.id})`}
                value={selectedChamberRoom}
                onChange={handleChamberRoomChange}
                renderInput={(params) => (
                  <TextField {...params} label="Chamber Room" size="small" />
                )}
                loading={loadingOptions}
                noOptionsText="No chamber rooms found"
              />
            </FormControl>

            {/* Mentor to Audit - Dropdown */}
            <FormControl size="small" fullWidth>
              <Autocomplete
                options={allMentors}
                getOptionLabel={(option) => option.name || option.id || 'Unknown'}
                value={selectedMentorToAudit}
                onChange={handleMentorToAuditChange}
                renderInput={(params) => (
                  <TextField {...params} label="Mentor to Audit" size="small" />
                )}
                loading={loadingOptions}
                noOptionsText="No mentors found"
              />
            </FormControl>

            {/* Auditor Mentor - Dropdown (hidden if from therapists) */}
            {!isFromTherapists && (
              <FormControl size="small" fullWidth>
                <Autocomplete
                  options={allMentors}
                  getOptionLabel={(option) => option.name || option.id || 'Unknown'}
                  value={selectedAuditorMentor}
                  onChange={handleAuditorMentorChange}
                  renderInput={(params) => (
                    <TextField {...params} label="Auditor Mentor" size="small" />
                  )}
                  loading={loadingOptions}
                  noOptionsText="No mentors found"
                />
              </FormControl>
            )}

            {/* Request Status - Dropdown */}
            <FormControl size="small" fullWidth>
              <InputLabel>Request Status</InputLabel>
              <Select
                multiple
                value={filters.requestStatuses}
                onChange={handleStatusChange}
                label="Request Status"
                renderValue={(selected) => selected.join(', ') || 'All'}
              >
                {REQUEST_STATUSES.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Created Date Range */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                type="datetime-local"
                label="Created After"
                value={filters.createdAfter || ''}
                onChange={(e) => handleDateRangeChange('createdAfter', e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1, minWidth: 200 }}
              />
              <TextField
                size="small"
                type="datetime-local"
                label="Created Before"
                value={filters.createdBefore || ''}
                onChange={(e) => handleDateRangeChange('createdBefore', e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1, minWidth: 200 }}
              />
            </Box>
          </Stack>
        </FiltersContainer>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Table */}
        <StyledTableContainer component={Paper}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
            <Typography variant="h6" fontWeight={600}>
              Audit Requests ({pagination.totalElements})
            </Typography>
            <Tooltip title="Refresh">
              <IconButton onClick={() => fetchAuditRequests(pagination.page)} disabled={loading}>
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : auditRequests.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Typography variant="body2" color="text.secondary">
                {hasActiveFilters
                  ? 'No audit requests found for the selected filters.'
                  : 'Start by searching or adjust filters.'}
              </Typography>
            </Box>
          ) : (
            <>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Request ID</TableCell>
                    <TableCell>Chamber Room</TableCell>
                    <TableCell>Clinic Brand</TableCell>
                    <TableCell>Clinic Location</TableCell>
                    <TableCell>Mentor to Audit</TableCell>
                    <TableCell>Auditor</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created Time</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auditRequests.map((request) => (
                    <TableRow key={request.id} hover>
                      <TableCell>{request.id}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {request.chamberRoomTitle || '-'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {request.chamberRoomId}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">{request.clinicBrandTitle || '-'}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {request.clinicBrandSlug || '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {request.clinicLocationTitle || '-'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {request.clinicLocationSlug || '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{request.mentorToAuditId || '-'}</TableCell>
                      <TableCell>{request.auditorMentorId || '-'}</TableCell>
                      <TableCell>
                        <StatusChip
                          label={request.requestStatus}
                          status={request.requestStatus}
                          size="small"
                        />
                        {request.approvalExpiry && isExpired(request.approvalExpiry) && (
                          <Chip
                            label="Expired"
                            size="small"
                            color="error"
                            sx={{ ml: 0.5, fontSize: '0.65rem' }}
                          />
                        )}
                      </TableCell>
                      <TableCell>{formatDateTime(request.createdTime)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleViewDetails(request)}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {request.requestStatus === 'PENDING' && (
                            <Tooltip title="Approve/Reject">
                              <IconButton
                                size="small"
                                onClick={() => handleApproveReject(request)}
                                color="primary"
                              >
                                <CheckCircle fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <Pagination
                    count={pagination.totalPages}
                    page={pagination.page + 1}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </StyledTableContainer>

        {/* Details Drawer */}
        <AuditRequestDetailsDrawer
          open={detailsDrawerOpen}
          onClose={() => {
            setDetailsDrawerOpen(false)
            setSelectedRequest(null)
          }}
          request={selectedRequest}
          onApproveReject={handleApproveReject}
        />

        {/* Approve/Reject Dialog */}
        <ApproveRejectDialog
          open={approveRejectDialogOpen}
          onClose={() => {
            setApproveRejectDialogOpen(false)
            setRequestToApproveReject(null)
          }}
          request={requestToApproveReject}
          onSuccess={handleApproveRejectSuccess}
        />
      </Box>
    </>
  )
}
